// Color & ColorSolver classes from https://stackoverflow.com/a/43960991

import type { ColorItem } from 'webapp-types';

class Color {
	r!: number;
	g!: number;
	b!: number;

	constructor(hex: string) {
		const rgb = hexToRgbAndOpacity(hex);
		this.set(rgb.r, rgb.g, rgb.b);
	}

	toString() {
		return `rgb(${Math.round(this.r)}, ${Math.round(this.g)}, ${Math.round(this.b)})`;
	}

	set(r: number, g: number, b: number) {
		this.r = this.clamp(r);
		this.g = this.clamp(g);
		this.b = this.clamp(b);
	}

	hueRotate(angle = 0) {
		angle = (angle / 180) * Math.PI;
		const sin = Math.sin(angle);
		const cos = Math.cos(angle);

		this.multiply([
			0.213 + cos * 0.787 - sin * 0.213,
			0.715 - cos * 0.715 - sin * 0.715,
			0.072 - cos * 0.072 + sin * 0.928,
			0.213 - cos * 0.213 + sin * 0.143,
			0.715 + cos * 0.285 + sin * 0.14,
			0.072 - cos * 0.072 - sin * 0.283,
			0.213 - cos * 0.213 - sin * 0.787,
			0.715 - cos * 0.715 + sin * 0.715,
			0.072 + cos * 0.928 + sin * 0.072,
		]);
	}

	grayscale(value = 1) {
		this.multiply([
			0.2126 + 0.7874 * (1 - value),
			0.7152 - 0.7152 * (1 - value),
			0.0722 - 0.0722 * (1 - value),
			0.2126 - 0.2126 * (1 - value),
			0.7152 + 0.2848 * (1 - value),
			0.0722 - 0.0722 * (1 - value),
			0.2126 - 0.2126 * (1 - value),
			0.7152 - 0.7152 * (1 - value),
			0.0722 + 0.9278 * (1 - value),
		]);
	}

	sepia(value = 1) {
		this.multiply([
			0.393 + 0.607 * (1 - value),
			0.769 - 0.769 * (1 - value),
			0.189 - 0.189 * (1 - value),
			0.349 - 0.349 * (1 - value),
			0.686 + 0.314 * (1 - value),
			0.168 - 0.168 * (1 - value),
			0.272 - 0.272 * (1 - value),
			0.534 - 0.534 * (1 - value),
			0.131 + 0.869 * (1 - value),
		]);
	}

	saturate(value = 1) {
		this.multiply([
			0.213 + 0.787 * value,
			0.715 - 0.715 * value,
			0.072 - 0.072 * value,
			0.213 - 0.213 * value,
			0.715 + 0.285 * value,
			0.072 - 0.072 * value,
			0.213 - 0.213 * value,
			0.715 - 0.715 * value,
			0.072 + 0.928 * value,
		]);
	}

	multiply(matrix: number[]) {
		const newR = this.clamp(this.r * matrix[0]! + this.g * matrix[1]! + this.b * matrix[2]!);
		const newG = this.clamp(this.r * matrix[3]! + this.g * matrix[4]! + this.b * matrix[5]!);
		const newB = this.clamp(this.r * matrix[6]! + this.g * matrix[7]! + this.b * matrix[8]!);
		this.r = newR;
		this.g = newG;
		this.b = newB;
	}

	brightness(value = 1) {
		this.linear(value);
	}
	contrast(value = 1) {
		this.linear(value, -(0.5 * value) + 0.5);
	}

	linear(slope = 1, intercept = 0) {
		this.r = this.clamp(this.r * slope + intercept * 255);
		this.g = this.clamp(this.g * slope + intercept * 255);
		this.b = this.clamp(this.b * slope + intercept * 255);
	}

	invert(value = 1) {
		this.r = this.clamp((value + (this.r / 255) * (1 - 2 * value)) * 255);
		this.g = this.clamp((value + (this.g / 255) * (1 - 2 * value)) * 255);
		this.b = this.clamp((value + (this.b / 255) * (1 - 2 * value)) * 255);
	}

	hsl() {
		// Code taken from https://stackoverflow.com/a/9493060/2688027, licensed under CC BY-SA.
		const r = this.r / 255;
		const g = this.g / 255;
		const b = this.b / 255;
		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		const l = (max + min) / 2;
		let h = 0,
			s = 0;

		if (max !== min) {
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
			switch (max) {
				case r:
					h = (g - b) / d + (g < b ? 6 : 0);
					break;

				case g:
					h = (b - r) / d + 2;
					break;

				case b:
					h = (r - g) / d + 4;
					break;
			}
			h /= 6;
		}

		return {
			h: h * 100,
			s: s * 100,
			l: l * 100,
		};
	}

	clamp(value: number) {
		if (value > 255) {
			value = 255;
		} else if (value < 0) {
			value = 0;
		}
		return value;
	}
}

interface SPSAReturn {
	values: number[];
	loss: number;
}

class ColorSolver {
	target: Color;
	targetHSL: { h: number; s: number; l: number };

	reusedColor: Color;

	constructor(color: Color) {
		this.target = color;
		this.targetHSL = color.hsl();
		this.reusedColor = new Color('#000000');
	}

	solve() {
		const result = this.solveNarrow(this.solveWide());
		return {
			values: result.values,
			loss: result.loss,
			filter: this.css(result.values),
		};
	}

	solveWide(): SPSAReturn {
		const A = 5;
		const c = 15;
		const a = [60, 180, 18000, 600, 1.2, 1.2];

		let best: SPSAReturn = { loss: Infinity, values: [] };
		for (let i = 0; best.loss > 25 && i < 3; i++) {
			const initial = [50, 20, 3750, 50, 100, 100];
			const result = this.spsa(A, a, c, initial, 1000);
			if (result.loss < best.loss) {
				best = result;
			}
		}
		return best;
	}

	solveNarrow(wide: SPSAReturn) {
		const A = wide.loss;
		const c = 2;
		const A1 = A + 1;
		const a = [0.25 * A1, 0.25 * A1, A1, 0.25 * A1, 0.2 * A1, 0.2 * A1];
		return this.spsa(A, a, c, wide.values, 500);
	}

	spsa(A: number, a: number[], c: number, values: number[], iters: number): SPSAReturn {
		const alpha = 1;
		const gamma = 0.16666666666666666;

		let best: number[] = [];
		let bestLoss = Infinity;
		const deltas = new Array<number>(6);
		const highArgs = new Array<number>(6);
		const lowArgs = new Array<number>(6);

		for (let k = 0; k < iters; k++) {
			const ck = c / Math.pow(k + 1, gamma);
			for (let i = 0; i < 6; i++) {
				deltas[i] = Math.random() > 0.5 ? 1 : -1;
				highArgs[i] = values[i]! + ck * deltas[i]!;
				lowArgs[i] = values[i]! - ck * deltas[i]!;
			}

			const lossDiff = this.loss(highArgs) - this.loss(lowArgs);
			for (let i = 0; i < 6; i++) {
				const g = (lossDiff / (2 * ck)) * deltas[i]!;
				const ak = a[i]! / Math.pow(A + k + 1, alpha);
				values[i] = fix(values[i]! - ak * g, i);
			}

			const loss = this.loss(values);
			if (loss < bestLoss) {
				best = values.slice(0);
				bestLoss = loss;
			}
		}
		return { values: best, loss: bestLoss };

		function fix(value: number, idx: number) {
			let max = 100;
			if (idx === 2 /* saturate */) {
				max = 7500;
			} else if (idx === 4 /* brightness */ || idx === 5 /* contrast */) {
				max = 200;
			}

			if (idx === 3 /* hue-rotate */) {
				if (value > max) {
					value %= max;
				} else if (value < 0) {
					value = max + (value % max);
				}
			} else if (value < 0) {
				value = 0;
			} else if (value > max) {
				value = max;
			}
			return value;
		}
	}

	loss(filters: number[]) {
		// Argument is array of percentages.
		const color = this.reusedColor;
		color.set(0, 0, 0);

		color.invert(filters[0]! / 100);
		color.sepia(filters[1]! / 100);
		color.saturate(filters[2]! / 100);
		color.hueRotate(filters[3]! * 3.6);
		color.brightness(filters[4]! / 100);
		color.contrast(filters[5]! / 100);

		const colorHSL = color.hsl();
		return (
			Math.abs(color.r - this.target.r) +
			Math.abs(color.g - this.target.g) +
			Math.abs(color.b - this.target.b) +
			Math.abs(colorHSL.h - this.targetHSL.h) +
			Math.abs(colorHSL.s - this.targetHSL.s) +
			Math.abs(colorHSL.l - this.targetHSL.l)
		);
	}

	css(filters: number[]) {
		function fmt(idx: number, multiplier = 1) {
			return Math.round(filters[idx]! * multiplier);
		}
		return `filter: invert(${fmt(0)}%) sepia(${fmt(1)}%) saturate(${fmt(2)}%) hue-rotate(${fmt(3, 3.6)}deg) brightness(${fmt(4)}%) contrast(${fmt(5)}%);`;
	}
}

function hexToRgbAndOpacity(
	hex: string,
	opacity?: number,
): { r: number; g: number; b: number; a: number } {
	// svg linearGradient definitions cannot handle rgba with alpha in Safari

	// Remove hash
	hex = (hex || '').slice(1);

	// Split to four channels
	const c = hex.match(/.{1,2}/g) || [],
		a = ((parseInt(c[3] || 'FF', 16) / 255) * 1000) / 1000;

	if (!opacity) {
		opacity = 1;
	}

	// multiply the opacities, if both given
	opacity *= a;
	opacity = Math.round(opacity * 100) / 100;

	return {
		r: parseInt(c[0] || 'FF', 16),
		g: parseInt(c[1] || 'FF', 16),
		b: parseInt(c[2] || 'FF', 16),
		a: opacity,
	};
}

function hexToRgba(hex: string, opacity: number): string {
	const rgb = hexToRgbAndOpacity(hex, opacity);

	return `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`;
}

// http://stackoverflow.com/a/13532993/4510606
function shadeColor(hex: string, percent: number): string {
	const rgb = hexToRgbAndOpacity(hex);

	let R = (rgb.r * (100 + percent)) / 100;
	let G = (rgb.g * (100 + percent)) / 100;
	let B = (rgb.b * (100 + percent)) / 100;

	R = R < 255 ? R : 255;
	G = G < 255 ? G : 255;
	B = B < 255 ? B : 255;

	return `rgba(${R},${G},${B},${rgb.a})`;
}

function getGradient(mainColor: string, configColors: ColorItem[]): string {
	const fromConfig = configColors.find((item) => item.hex === mainColor);
	let style = 'background:' + mainColor + ';',
		lightColor,
		darkColor;

	if (fromConfig) {
		lightColor = fromConfig.top;
		darkColor = fromConfig.bottom;
	} else {
		lightColor = shadeColor(mainColor, 20);
		darkColor = shadeColor(mainColor, -40);
	}

	style += `background:linear-gradient(145deg, ${lightColor}, ${mainColor} 40%, ${darkColor});`;
	return style;
}

function getFilterCssForColor(hex: string): string {
	const color = new Color(hex);
	const solver = new ColorSolver(color);
	const result = solver.solve();

	if (result.loss > 10) {
		// redoing again
		return getFilterCssForColor(hex);
	}

	return result.filter;
}

function isTextColorLight(hex: string) {
	// Dark text for light background, light text for dark background
	const rgb = hexToRgbAndOpacity(hex);

	// Calculate luminance
	const luminance = (0.2126 * rgb.r) / 255 + (0.7152 * rgb.g) / 255 + (0.0722 * rgb.b) / 255;

	// Choose text color based on luminance
	return luminance > 0.5;
}

function isBlackOrWhiteColor(colorValue: string): boolean {
	const normalizedColor = colorValue.trim().toLowerCase();

	if (normalizedColor === 'black' || normalizedColor === 'white') {
		return true;
	}

	const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(normalizedColor);
	if (hexMatch?.[1]) {
		let hex = hexMatch[1];
		// expand shorthand hex (#fff -> #ffffff)
		if (hex.length === 3) {
			// charAt to satisfy type checker
			hex =
				hex.charAt(0) +
				hex.charAt(0) +
				hex.charAt(1) +
				hex.charAt(1) +
				hex.charAt(2) +
				hex.charAt(2);
		}
		return hex === '000000' || hex === 'ffffff';
	}

	const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(normalizedColor);
	if (rgbMatch?.[1] && rgbMatch[2] && rgbMatch[3]) {
		const r = parseInt(rgbMatch[1], 10);
		const g = parseInt(rgbMatch[2], 10);
		const b = parseInt(rgbMatch[3], 10);
		return (r === 0 && g === 0 && b === 0) || (r === 255 && g === 255 && b === 255);
	}

	return false;
}

export {
	hexToRgbAndOpacity,
	hexToRgba,
	shadeColor,
	getGradient,
	getFilterCssForColor,
	isTextColorLight,
	isBlackOrWhiteColor,
};
